import mustache from "mustache";
import { BaseResponse } from "../../../../base/response/BaseResponse";
import {
  AutocompleteConfig,
  AutocompleteOption,
} from "../../../definitions/form/autocomplete/AutocompleteConfig";
import { IConnectRender } from "../../../definitions/IConnectRender";
import template from "./template.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let AUTOCOMPLETE_ID = 0;

/** Máximo de resultados por defecto. */
const DEFAULT_MAX_RESULTS = 50;

/** Autocomplete (Render/Connect): input que filtra un catálogo al escribir y
 *  muestra un dropdown de opciones (título + subtítulo + badge); al elegir una
 *  entrega el item (`onPick`), limpia el input y cierra. El dropdown va en
 *  flujo (no absoluto) para no recortarse dentro de cards con overflow. */
export class AutocompleteRender<T extends BaseResponse>
  implements IConnectRender
{
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _autocompleteId: number;

  constructor(private readonly _config: AutocompleteConfig<T>) {
    AUTOCOMPLETE_ID += 1;
    this._autocompleteId = AUTOCOMPLETE_ID;
  }

  render(): string {
    return mustache.render(template, {
      id: this._autocompleteId,
      placeholder: this._config.placeholder,
    });
  }

  /** Captura elementos, cablea el input y carga el catálogo. */
  connect(): void {
    this._input = document.getElementById(
      `autocomplete-input-${this._autocompleteId}`,
    ) as HTMLInputElement | null;
    this._dropdown = document.getElementById(
      `autocomplete-dropdown-${this._autocompleteId}`,
    );

    this._input?.addEventListener("input", this._onInput);
    // Al enfocar se muestran opciones; al perder el foco se ocultan.
    this._input?.addEventListener("focus", this._onInput);
    this._input?.addEventListener("blur", this._onBlur);
    // Delegado: un solo listener para todas las opciones del dropdown. El
    // mousedown previene el blur del input para que el click llegue a tiempo.
    this._dropdown?.addEventListener("mousedown", this._onDropdownMouseDown);
    this._dropdown?.addEventListener("click", this._onOptionClick);

    void this._loadAsync();
  }

  dispose(): void {
    this._disposed = true;
    this._input?.removeEventListener("input", this._onInput);
    this._input?.removeEventListener("focus", this._onInput);
    this._input?.removeEventListener("blur", this._onBlur);
    this._dropdown?.removeEventListener("mousedown", this._onDropdownMouseDown);
    this._dropdown?.removeEventListener("click", this._onOptionClick);
  }

  /** Enfoca el input. */
  focus(): void {
    this._input?.focus();
  }

  /** Deshabilita el input (y cierra el dropdown). */
  disabled(): void {
    if (this._input) this._input.disabled = true;
    this._hideDropdown();
  }

  /** Habilita el input. */
  enabled(): void {
    if (this._input) this._input.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _dropdown: HTMLElement | null = null;
  /** Catálogo completo cargado. */
  private _items: T[] = [];
  /** Items actualmente visibles en el dropdown (paralelo a los data-index). */
  private _visible: T[] = [];
  private _disposed = false;

  /** Carga el catálogo (una vez; el servicio ya cachea en IndexedDB). */
  private async _loadAsync(): Promise<void> {
    const items = await this._config.service.getListAsync();
    if (this._disposed) return;
    this._items = items;
  }

  /** Filtra y muestra el dropdown según el texto. El filtro del config se
   *  aplica SIEMPRE (también con texto vacío, al enfocar): así el consumidor
   *  puede excluir opciones — p. ej. productos ya agregados. */
  private _onInput = (): void => {
    const texto = (this._input?.value ?? "").trim().toLowerCase();
    const maxResults = this._config.maxResults ?? DEFAULT_MAX_RESULTS;
    this._visible = this._items
      .filter((item) => this._config.filter(item, texto))
      .slice(0, maxResults);
    this._renderOptions();
  };

  /** Al perder el foco: cierra el dropdown (el mousedown de las opciones lo
   *  previene para que el pick alcance a ejecutarse). */
  private _onBlur = (): void => {
    this._hideDropdown();
  };

  /** Previene el blur del input al clickear dentro del dropdown. */
  private _onDropdownMouseDown = (e: Event): void => {
    e.preventDefault();
  };

  /** Pinta las opciones visibles (o cierra si no hay resultados). */
  private _renderOptions(): void {
    if (!this._dropdown) return;
    if (this._visible.length === 0) {
      this._dropdown.innerHTML = `<div class="sx-autocomplete-empty">Sin resultados</div>`;
      this._dropdown.style.display = "";
      return;
    }

    this._dropdown.innerHTML = this._visible
      .map((item, index) =>
        this._optionHtml(this._config.getOption(item), index),
      )
      .join("");
    this._dropdown.style.display = "";
  }

  /** HTML de una opción (título + subtítulo + badge). */
  private _optionHtml(option: AutocompleteOption, index: number): string {
    const subtitle = option.subtitle
      ? `<div class="sx-autocomplete-subtitle">${option.subtitle}</div>`
      : "";
    const badge = option.badge
      ? `<span class="sx-autocomplete-badge">${option.badge}</span>`
      : "";
    return `
      <div class="sx-autocomplete-option" data-index="${index}">
        <div class="sx-autocomplete-info">
          <div class="sx-autocomplete-title">${option.title}</div>
          ${subtitle}
        </div>
        ${badge}
      </div>`;
  }

  /** Delegado del click en opciones: entrega el item, limpia y cierra. */
  private _onOptionClick = (e: Event): void => {
    const optionEl = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-index]",
    );
    if (!optionEl) return;
    const item = this._visible[Number(optionEl.dataset.index)];
    if (!item) return;

    if (this._input) this._input.value = "";
    this._hideDropdown();
    this._config.onPick(item);
  };

  /** Cierra el dropdown y descarta los visibles. */
  private _hideDropdown(): void {
    if (this._dropdown) {
      this._dropdown.style.display = "none";
      this._dropdown.innerHTML = "";
    }
    this._visible = [];
  }
}
